import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  SwipeableDrawer, Box, Typography, Button, Stack,
  useTheme, useMediaQuery,
} from '@mui/material';
import { BOTTOM_SHEET_TRANSITION } from '../../theme/tokens';

/**
 * ActionSheet — responsive modal container.
 *   Mobile  → SwipeableDrawer bottom sheet with handle bar, full-width CTA.
 *   Desktop → standard MUI Dialog.
 *
 * Props:
 *   open            — bool
 *   onClose         — fn()
 *   title           — string
 *   children        — form body / content
 *   primaryLabel    — CTA button label
 *   onPrimary       — async fn() called on CTA click
 *   primaryDisabled — bool
 *   primaryColor    — MUI colour of the CTA (default `primary`; `error` for a
 *                     destructive choice, so it does not look like the
 *                     recommended one)
 *   secondaryLabel  — the dismissing button's label (default `Cancel`).
 *                     🔴 It exists because a sheet opened FROM a modal needs a
 *                     name of its own: two buttons called `Cancel` in the same
 *                     document are two tab stops with the same announcement,
 *                     and the reader cannot tell which one keeps the work.
 *                     The house wording for "do not discard" is
 *                     `Keep editing`.
 */
export default function ActionSheet({
  open,
  onClose,
  title,
  children,
  primaryLabel,
  onPrimary,
  primaryDisabled = false,
  primaryColor = 'primary',
  secondaryLabel = 'Cancel',
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (isMobile) {
    return (
      <SwipeableDrawer
        anchor="bottom"
        open={open}
        onOpen={() => {}}
        onClose={onClose}
        disableSwipeToOpen
        {...BOTTOM_SHEET_TRANSITION}
        PaperProps={{ sx: { borderRadius: '16px 16px 0 0', overflow: 'hidden' } }}
      >
        {/* Handle bar */}
        <Box sx={{ width: 36, height: 4, bgcolor: 'divider', borderRadius: 99, mx: 'auto', mt: 1.5, mb: 0.5 }} />

        {/* Title */}
        <Typography variant="h6" fontWeight={600} sx={{ px: 2.5, pt: 1, pb: 0.5 }}>
          {title}
        </Typography>

        {/* Content */}
        <Box sx={{ px: 2.5, pt: 1.25, pb: 0.5 }}>
          {children}
        </Box>

        {/* Full-width actions */}
        <Box sx={{ px: 2.5, pt: 1.5, pb: 4 }}>
          <Stack spacing={1}>
            <Button
              variant="contained"
              color={primaryColor}
              fullWidth
              disabled={primaryDisabled}
              onClick={onPrimary}
              sx={{ py: 1.25 }}
            >
              {primaryLabel}
            </Button>
            <Button
              variant="text"
              fullWidth
              onClick={onClose}
              sx={{ color: 'text.secondary' }}
            >
              {secondaryLabel}
            </Button>
          </Stack>
        </Box>
      </SwipeableDrawer>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ pt: '12px !important' }}>{children}</DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{secondaryLabel}</Button>
        <Button variant="contained" color={primaryColor} disabled={primaryDisabled} onClick={onPrimary}>
          {primaryLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
